import { FC, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from "@mui/material";

import { RiInformationLine } from "react-icons/ri";

import {
  Claim,
  FeeChange,
  ManualPaymentFields,
} from "../../../../types/claims";
import { apiService } from "../../../../services/apiService";
import useNotifier from "../../../../hooks/useNotifier";
import CoreTextarea from "../../../ui/core/CoreTextarea";
import { ClaimEditError } from "./claimAppeals";
import {
  RemovePaymentErrorCode,
  describeManualPayment,
  parseRemovePaymentError,
} from "./record-payment/manualPayments";
import {
  EditErrorAlert,
  FieldMessage,
  SummaryRow,
} from "./record-payment/ClaimEditDialogParts";
import PaymentEffect from "./record-payment/PaymentEffect";

interface RemovePaymentDialogProps {
  open: boolean;
  claim: Claim;
  attachmentId: string;
  payment: ManualPaymentFields;
  onClose: () => void;
  /** Called once it is removed, or when the API says it's already gone
   * (removed or joined to a remit), so the caller refreshes the claim. */
  onRemoved: () => void;
}

type Step = "form" | "confirm";

type RemoveError = ClaimEditError<RemovePaymentErrorCode, "reason">;

const isGone = (error: RemoveError) =>
  error.code === "not_found" || error.code === "payment_not_active";

/**
 * Admin-only: removes a manual payment, with a required reason, in two steps.
 * Review asks the API for a dry run (every check, nothing saved) and shows its
 * effect on the claim; only confirming removes it. The claim log keeps it.
 */
const RemovePaymentDialog: FC<RemovePaymentDialogProps> = ({
  open,
  claim,
  attachmentId,
  payment,
  onClose,
  onRemoved,
}) => {
  const theme = useTheme();
  const notifier = useNotifier();

  const [step, setStep] = useState<Step>("form");
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | undefined>();
  const [fee, setFee] = useState<FeeChange | null>(null);
  const [error, setError] = useState<RemoveError | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  // Start fresh each time the dialog opens
  useEffect(() => {
    if (!open) return;
    setStep("form");
    setReason("");
    setReasonError(undefined);
    setFee(null);
    setError(null);
    setIsChecking(false);
    setIsRemoving(false);
  }, [open]);

  const remove = (dryRun: boolean) =>
    apiService.removeClaimManualPayment(
      claim.id,
      attachmentId,
      reason.trim(),
      dryRun,
    );

  /** A refusal: a reason error goes back to the form, a payment that's already
   * gone refreshes the claim, anything else shows on the summary. */
  const handleRefusal = (refusal: RemoveError) => {
    if (refusal.fieldErrors.reason) {
      setReasonError(refusal.fieldErrors.reason);
      setStep("form");
      return;
    }
    if (isGone(refusal)) {
      notifier.showError(refusal.message);
      onRemoved();
      onClose();
      return;
    }
    setError(refusal);
    setStep("confirm");
  };

  const handleReview = async () => {
    if (!reason.trim()) {
      setReasonError("Enter why the payment is being removed.");
      return;
    }
    setIsChecking(true);
    setError(null);
    try {
      const response = await remove(true);
      if (response.success && response.data) {
        setFee(response.data.fee);
        setStep("confirm");
        return;
      }
      handleRefusal(parseRemovePaymentError(response));
    } finally {
      setIsChecking(false);
    }
  };

  const handleRemove = async () => {
    setIsRemoving(true);
    setError(null);
    try {
      const response = await remove(false);
      if (response.success) {
        notifier.showSuccess("Payment removed.");
        onRemoved();
        onClose();
        return;
      }
      handleRefusal(parseRemovePaymentError(response));
    } finally {
      setIsRemoving(false);
    }
  };

  // A late check or remove response would land on a closed dialog
  const handleClose = () => {
    if (isRemoving || isChecking) return;
    onClose();
  };

  const isBlocked = !!error;

  const renderForm = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
      <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
        The payment stops counting towards the claim and leaves the timeline.
        The claim log keeps a record of it and of why it was removed.
      </Typography>
      <Box>
        <CoreTextarea
          title="Reason"
          placeholder="Why is this payment being removed?"
          value={reason}
          minRows={3}
          maxRows={6}
          disabled={isChecking}
          onChange={(e) => {
            setReason(e.target.value);
            if (e.target.value.trim()) setReasonError(undefined);
          }}
          aria-label="Reason"
        />
        <FieldMessage error={reasonError}>Required.</FieldMessage>
      </Box>
    </Box>
  );

  const renderConfirm = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
      {!isBlocked && (
        <Alert
          severity="warning"
          icon={<RiInformationLine size={20} />}
          sx={{ maxWidth: "100%" }}
        >
          This can't be undone.
        </Alert>
      )}

      <Box>
        {describeManualPayment(payment).map((row) => (
          <SummaryRow key={row.label} label={row.label}>
            {row.value}
          </SummaryRow>
        ))}
        <SummaryRow label="Reason">{reason.trim()}</SummaryRow>
      </Box>

      {!isBlocked && <PaymentEffect claim={claim} fee={fee} />}

      {error && <EditErrorAlert message={error.message} />}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          fontSize: "19px",
        }}
      >
        {step === "form" ? "Remove payment" : "Confirm removal"}
        <Typography
          component="div"
          sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
        >
          Claim {claim.claimNumber}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {step === "form" ? renderForm() : renderConfirm()}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        {step === "form" ? (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              disabled={isChecking}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReview}
              variant="contained"
              loading={isChecking}
            >
              Review
            </Button>
          </>
        ) : isBlocked ? (
          <Button onClick={handleClose} variant="outlined">
            Close
          </Button>
        ) : (
          <>
            <Button
              onClick={() => setStep("form")}
              variant="outlined"
              disabled={isRemoving}
            >
              Back
            </Button>
            <Button
              onClick={handleRemove}
              variant="contained"
              color="error"
              loading={isRemoving}
            >
              Confirm and remove
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RemovePaymentDialog;
